import { env } from '@/lib/env';
import { handleError } from '@/lib/error/handle';
import { mutate } from 'swr';
import { toast } from 'sonner';
import { useGenerationPolling, type PollingResponseContext, type UseMediaPollingParams } from '@/components/nodes/shared/use-generation-polling';
import { reuseOrAppendHistory } from '@/components/nodes/shared/media-history';
import { notifyAssetLibraryChanged } from '@/components/canvas/utils/asset-folders-events';

export type UseVideoPollingParams = UseMediaPollingParams;

function handleResponse(
    s: { status: string; outputUrl?: string; error?: string; processingTaskId?: string },
    ctx: PollingResponseContext,
): 'break' | 'continue' {
    if (s.processingTaskId) {
        try { ctx.updateNodeData(ctx.nodeId, { processingTaskId: s.processingTaskId }); } catch {}
    }

    if (s.status === 'completed' && s.outputUrl) {
        const nowIso = new Date().toISOString();
        const ld = ctx.getLatestData();
        const { history, activeHistoryId } = reuseOrAppendHistory(
            ld?.history,
            [s.outputUrl],
            (id) => ({
                id,
                url: s.outputUrl,
                type: 'video/mp4',
                createdAt: nowIso,
                prompt: ld?.instructions ?? '',
                modelId: ctx.modelId,
                duration: ld?.duration,
                aspectRatio: ld?.aspectRatio,
                quality: ld?.quality,
                modelUi: ld?.modelUi,
            }),
        );
        try {
            ctx.updateNodeData(ctx.nodeId, {
                updatedAt: nowIso,
                status: 'completed',
                model: ctx.modelId,
                generated: { url: s.outputUrl, type: 'video/mp4' },
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
                history,
                activeHistoryId,
                // 样片转正式完成后回归普通正式节点
                draftSource: undefined,
            });
        } catch {}
        try { toast.success(ctx.t('Video generated successfully')); } catch {}
        notifyAssetLibraryChanged();
        setTimeout(() => mutate(ctx.projectId ? `credits:${ctx.projectId}` : 'credits'), 5000);
        return 'break';
    }

    if (s.status === 'failed') {
        try {
            ctx.updateNodeData(ctx.nodeId, {
                status: 'failed',
                updatedAt: new Date().toISOString(),
                error: s.error || 'Video generation failed',
                processingGenerationId: undefined,
                runtimeEstimatedTime: undefined,
            });
        } catch {}
        try {
            if (String(env.NEXT_PUBLIC_ENABLE_REALTIME ?? 'true') === 'false') {
                handleError(ctx.t('Video generation failed'), new Error(s.error || 'Status polling reported failed'));
            }
        } catch {}
        return 'break';
    }

    return 'continue';
}

export function useVideoPolling(params: UseVideoPollingParams) {
    useGenerationPolling({
        ...params,
        statusEndpoint: '/api/video/status',
        getPollInterval: () => 5 * 60_000,
        handleResponse,
    });
}